import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUserById, deleteUser } from '../../services/users';
import { getTaxCasesByClient } from '../../services/cases';
import { formatDate } from '../../lib/format';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { ApiClientError } from '../../services/api';

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [casesPage, setCasesPage] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data: client, isLoading: clientLoading, error: clientError } = useQuery({
    queryKey: ['client', id],
    queryFn: () => getUserById(id!),
    enabled: !!id,
  });

  const { data: casesData, isLoading: casesLoading } = useQuery({
    queryKey: ['client-cases', id, casesPage],
    queryFn: () => getTaxCasesByClient(id!, casesPage, 10),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      navigate('/admin/clients', { 
        state: { message: 'User deleted successfully' } 
      });
    },
    onError: (error) => {
      const message = error instanceof ApiClientError 
        ? error.message 
        : 'Failed to delete user. Please try again.';
      setDeleteError(message);
    },
  });

  const handleDeleteClick = () => {
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = () => {
    if (id) {
      deleteMutation.mutate(id);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeleteError(null);
  };

  if (!id) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <span className="material-symbols-outlined text-6xl text-error">error</span>
          <h2 className="mt-4 text-2xl font-bold text-error">Invalid Client ID</h2>
          <Button onClick={() => navigate('/admin/clients')} className="mt-6">
            Back to Client Directory
          </Button>
        </div>
      </div>
    );
  }

  if (clientLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="inline-flex items-center gap-3 text-on-surface/60">
          <span className="material-symbols-outlined animate-spin">refresh</span>
          <span className="font-semibold">Loading client details...</span>
        </div>
      </div>
    );
  }

  if (clientError || !client) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <span className="material-symbols-outlined text-6xl text-error">error</span>
          <h2 className="mt-4 text-2xl font-bold text-error">Failed to Load Client</h2>
          <p className="mt-2 text-on-surface/70">
            {clientError instanceof Error ? clientError.message : 'Unable to load client details'}
          </p>
          <Button onClick={() => navigate('/admin/clients')} className="mt-6">
            Back to Client Directory
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <header className="mb-8 flex items-center justify-between gap-4">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <Link
              to="/admin/clients"
              className="flex items-center gap-1 text-sm font-semibold text-secondary transition-colors hover:text-primary"
            >
              <span className="material-symbols-outlined text-lg">arrow_back</span>
              Back to Clients
            </Link>
          </div>
          <h1 className="text-3xl font-bold text-primary sm:text-4xl lg:text-5xl">
            {client.firstName} {client.lastName}
          </h1>
          <p className="mt-2 text-base leading-relaxed text-on-surface/70 sm:text-lg">
            Client profile and case management
          </p>
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-1">
          <Card className="bg-surface-container-low">
            <CardHeader>
              <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-primary-container">
                <span className="material-symbols-outlined text-4xl text-on-primary-container">person</span>
              </div>
              <CardTitle>Client Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="label-sm text-on-surface/60">Full Name</p>
                <p className="font-semibold text-on-surface">
                  {client.firstName} {client.lastName}
                </p>
              </div>
              <div>
                <p className="label-sm text-on-surface/60">Email</p>
                <a
                  href={`mailto:${client.email}`}
                  className="font-semibold text-primary hover:underline"
                >
                  {client.email}
                </a>
              </div>
              {client.phone && (
                <div>
                  <p className="label-sm text-on-surface/60">Phone</p>
                  <a
                    href={`tel:${client.phone}`}
                    className="font-semibold text-primary hover:underline"
                  >
                    {client.phone}
                  </a>
                </div>
              )}
              <div>
                <p className="label-sm text-on-surface/60">Role</p>
                <span className={`chip ${client.role === 'ADMIN' ? 'chip-primary' : 'chip-tertiary'}`}>
                  {client.role}
                </span>
              </div>
              <div>
                <p className="label-sm text-on-surface/60">Auth Provider</p>
                <p className="font-semibold text-on-surface">{client.authProvider}</p>
              </div>
              <div>
                <p className="label-sm text-on-surface/60">Member Since</p>
                <p className="font-semibold text-on-surface">{formatDate(client.createdAt)}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-surface-container-low">
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant="secondary"
                className="w-full justify-start"
                onClick={() => window.location.href = `mailto:${client.email}`}
              >
                <span className="material-symbols-outlined mr-2">mail</span>
                Send Email
              </Button>
              {client.phone && (
                <Button
                  variant="secondary"
                  className="w-full justify-start"
                  onClick={() => window.location.href = `tel:${client.phone}`}
                >
                  <span className="material-symbols-outlined mr-2">call</span>
                  Call Client
                </Button>
              )}
            </CardContent>
          </Card>

          <Card className="border-2 border-error/20 bg-error-container/10">
            <CardHeader>
              <CardTitle className="text-error">Danger Zone</CardTitle>
              <CardDescription>
                Irreversible actions that permanently delete data
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="secondary"
                className="w-full justify-start border-2 border-error bg-error text-white hover:bg-error/90"
                onClick={handleDeleteClick}
              >
                <span className="material-symbols-outlined mr-2">delete_forever</span>
                Delete User & All Data
              </Button>
              <p className="mt-2 text-xs text-on-surface/60">
                This will permanently delete the user, all their tax cases, and all associated documents from the system and S3 storage.
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6 lg:col-span-2">
          <Card className="bg-surface-container-low">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Tax Cases</CardTitle>
                  <CardDescription>
                    All tax cases for this client
                  </CardDescription>
                </div>
                <Link to={`/admin/cases/new?clientId=${id}`}>
                  <Button>
                    <span className="material-symbols-outlined mr-2 text-sm">add</span>
                    New Case
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {casesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="inline-flex items-center gap-3 text-on-surface/60">
                    <span className="material-symbols-outlined animate-spin">refresh</span>
                    <span className="font-semibold">Loading cases...</span>
                  </div>
                </div>
              ) : !casesData || casesData.content.length === 0 ? (
                <div className="py-12 text-center">
                  <span className="material-symbols-outlined text-5xl text-on-surface/40">folder_open</span>
                  <p className="mt-4 font-semibold text-on-surface/70">No tax cases found</p>
                  <p className="mt-1 text-sm text-on-surface/60">
                    Create a new case to get started
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    {casesData.content.map((taxCase) => (
                      <Link
                        key={taxCase.id}
                        to={`/admin/cases/${taxCase.id}`}
                        className="block rounded-lg border border-outline-variant bg-surface-container p-4 transition-all hover:border-primary hover:shadow-md"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <h3 className="font-bold text-on-surface">
                                Tax Year {taxCase.taxYear}
                              </h3>
                              <span className={`chip chip-${
                                taxCase.status === 'COMPLETED' ? 'tertiary' :
                                taxCase.status === 'UNDER_REVIEW' || taxCase.status === 'CALCULATION' ? 'primary' :
                                'secondary'
                              }`}>
                                {taxCase.status.replace('_', ' ')}
                              </span>
                            </div>
                            {taxCase.notes && (
                              <p className="mt-1 text-sm text-on-surface/70 line-clamp-2">
                                {taxCase.notes}
                              </p>
                            )}
                            <div className="mt-3 flex flex-wrap gap-4 text-xs text-on-surface/60">
                              <span className="flex items-center gap-1">
                                <span className="material-symbols-outlined text-sm">calendar_today</span>
                                Created {formatDate(taxCase.createdAt)}
                              </span>
                              <span className="flex items-center gap-1">
                                <span className="material-symbols-outlined text-sm">update</span>
                                Updated {formatDate(taxCase.updatedAt)}
                              </span>
                            </div>
                          </div>
                          <span className="material-symbols-outlined text-on-surface/40">
                            chevron_right
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>

                  {casesData.totalPages > 1 && (
                    <div className="mt-6 flex items-center justify-between border-t border-outline-variant pt-4">
                      <p className="text-xs text-on-surface/60 sm:text-sm">
                        Page {casesPage + 1} of {casesData.totalPages}
                      </p>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setCasesPage(Math.max(0, casesPage - 1))}
                          disabled={casesPage === 0}
                          className="rounded-lg p-2 transition-colors hover:bg-surface-container-high disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <span className="material-symbols-outlined">chevron_left</span>
                        </button>
                        <button
                          onClick={() => setCasesPage(Math.min(casesData.totalPages - 1, casesPage + 1))}
                          disabled={casesPage >= casesData.totalPages - 1}
                          className="rounded-lg p-2 transition-colors hover:bg-surface-container-high disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <span className="material-symbols-outlined">chevron_right</span>
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-6 sm:grid-cols-2">
            <Card className="bg-surface-container-low">
              <CardHeader>
                <CardTitle>Total Cases</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-4xl font-bold text-primary">
                  {casesData?.totalElements || 0}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-surface-container-low">
              <CardHeader>
                <CardTitle>Active Cases</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-4xl font-bold text-primary">
                  {casesData?.content.filter(c => 
                    c.status !== 'COMPLETED' && c.status !== 'AWAITING_PAYMENT'
                  ).length || 0}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Modal
        open={showDeleteModal}
        onClose={handleDeleteCancel}
        title="Delete User & All Data"
      >
        <div className="space-y-6">
          <div className="rounded-lg bg-error-container p-4">
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-3xl text-error">warning</span>
              <div className="flex-1">
                <h3 className="font-bold text-error">This action cannot be undone!</h3>
                <p className="mt-2 text-sm text-on-error-container">
                  You are about to permanently delete:
                </p>
                <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-on-error-container">
                  <li>User account: <strong>{client?.firstName} {client?.lastName}</strong></li>
                  <li>All tax cases ({casesData?.totalElements || 0} total)</li>
                  <li>All documents and files from S3 storage</li>
                  <li>All payment records</li>
                  <li>All envelope and signature data</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="rounded-lg border-2 border-outline-variant bg-surface-container p-4">
            <p className="text-sm font-semibold text-on-surface">
              To confirm deletion, please type the user's email address:
            </p>
            <p className="mt-1 text-sm text-on-surface/70">
              <code className="rounded bg-surface-container-high px-2 py-1 font-mono">
                {client?.email}
              </code>
            </p>
          </div>

          {deleteError && (
            <div className="rounded-lg bg-error-container p-4">
              <p className="text-sm font-semibold text-error">{deleteError}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={handleDeleteCancel}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-error text-white hover:bg-error/90"
              onClick={handleDeleteConfirm}
              loading={deleteMutation.isPending}
            >
              <span className="material-symbols-outlined mr-2">delete_forever</span>
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
